<template>
	<div class="pagination-component">
		<!-- Modern flexible layout -->
		<div v-if="useModernLayout && totalPages > 1" class="pagination-wrapper" :class="alignmentClass">
			<div v-if="showInfo && (itemCount > 0 || totalItems > 0)" class="text-muted small">
				<slot name="info">
					{{ infoText }}
					<span v-if="hasSearch">(hasil pencarian)</span>
				</slot>
			</div>
			<nav v-if="!isLoading" aria-label="Page navigation">
				<ul class="pagination mb-0" :class="sizeClass">
					<li class="page-item" :class="{ disabled: currentPage === 1 }">
						<a
							class="page-link"
							href="#"
							@click.prevent="handlePageChange(currentPage - 1)"
							:tabindex="currentPage === 1 ? -1 : undefined"
							aria-label="Previous"
						>
							<i v-if="useIcons" class="material-icons" style="font-size: 18px;">chevron_left</i>
							<template v-else>
								<span aria-hidden="true">&laquo;</span>
								<span class="sr-only">Previous</span>
							</template>
						</a>
					</li>
					<li
						v-for="(page, index) in visiblePages"
						:key="typeof page === 'number' ? `page-${page}` : `ellipsis-${index}`"
						class="page-item"
						:class="{ active: page === currentPage }"
					>
						<span v-if="page === '...'" class="page-link">
							{{ page }}
						</span>
						<a
							v-else
							class="page-link"
							href="#"
							@click.prevent="handlePageChange(page)"
						>
							{{ page }}
						</a>
					</li>
					<li class="page-item" :class="{ disabled: currentPage === totalPages }">
						<a
							class="page-link"
							href="#"
							@click.prevent="handlePageChange(currentPage + 1)"
							:tabindex="currentPage === totalPages ? -1 : undefined"
							aria-label="Next"
						>
							<i v-if="useIcons" class="material-icons" style="font-size: 18px;">chevron_right</i>
							<template v-else>
								<span aria-hidden="true">&raquo;</span>
								<span class="sr-only">Next</span>
							</template>
						</a>
					</li>
				</ul>
			</nav>
		</div>

		<!-- Legacy layout (backward compatible) -->
		<div
			v-else-if="!useModernLayout && paginationPages > 1"
			class="block-footer mt-5 float-right">
			<nav
				v-if="!isLoading"
				aria-label="Page navigation example">
				<ul class="pagination">
					<li
						v-if="currentPage > 1"
						class="page-item">
						<a
							class="page-link"
							href="#"
							aria-label="Previous"
							@click.stop.prevent="handlePageChange(currentPage - 1)">
							<span aria-hidden="true">&laquo;</span>
							<span class="sr-only">Previous</span>
						</a>
					</li>
					<li
						v-for="(item, index) in visiblePaginationPages"
						:key="index"
						:class="{'active' : currentPage === item }"
						class="page-item">
						<span
							v-if="item === '...'"
							class="page-link">
							{{ item }}
						</span>
						<a
							v-else
							class="page-link"
							href="#"
							@click.stop.prevent="handlePageChange(item)">
							{{ item }}
						</a>
					</li>
					<li
						v-if="currentPage < parseInt(lastPage)"
						class="page-item">
						<a
							class="page-link"
							href="#"
							aria-label="Next"
							@click.stop.prevent="handlePageChange(currentPage + 1)">
							<span aria-hidden="true">&raquo;</span>
							<span class="sr-only">Next</span>
						</a>
					</li>
				</ul>
			</nav>
		</div>
	</div>
</template>

<script>
export default {
	name: 'Pagination',
	props: {
		// Legacy props (backward compatible)
		paginationPages: {
			type: Number,
			default: 0,
		},
		lastPage: {
			type: Number,
			default: 1,
		},

		// Modern props
		totalPages: {
			type: Number,
			default: 1,
		},
		totalItems: {
			type: Number,
			default: 0,
		},
		itemCount: {
			type: Number,
			default: 0,
		},
		maxVisiblePages: {
			type: Number,
			default: 5,
		},

		// Common props
		currentPage: {
			type: Number,
			required: true,
			default: 1,
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		autoScroll: {
			type: Boolean,
			default: true,
		},

		// New props for modern layout
		useModernLayout: {
			type: Boolean,
			default: false,
		},
		useIcons: {
			type: Boolean,
			default: false,
		},
		size: {
			type: String,
			default: 'sm',
			validator: value => ['', 'sm', 'md', 'lg'].includes(value),
		},
		showInfo: {
			type: Boolean,
			default: true,
		},
		hasSearch: {
			type: Boolean,
			default: false,
		},
		alignment: {
			type: String,
			default: 'between',
			validator: value => ['start', 'center', 'end', 'between'].includes(value),
		},
		infoText: {
			type: String,
			default: 'Menampilkan',
		},
	},
	computed: {
		sizeClass() {
			return this.size ? `pagination-${this.size}` : '';
		},
		alignmentClass() {
			return `justify-content-${this.alignment}`;
		},
		// Computed totalPages for backward compatibility
		computedTotalPages() {
			return this.useModernLayout ? this.totalPages : this.paginationPages;
		},
		visiblePages() {
			const { currentPage, maxVisiblePages } = this;
			const totalPages = this.computedTotalPages;
			const pages = [];

			if (totalPages <= maxVisiblePages) {
				// Show all pages if total is less than max
				for (let i = 1; i <= totalPages; i++) {
					pages.push(i);
				}
			} else {
				// Always show first page
				pages.push(1);

				if (currentPage <= 3) {
					// Near the beginning
					for (let i = 2; i <= 4; i++) {
						pages.push(i);
					}
					pages.push('...');
					pages.push(totalPages);
				} else if (currentPage >= totalPages - 2) {
					// Near the end
					pages.push('...');
					for (let i = totalPages - 3; i <= totalPages; i++) {
						pages.push(i);
					}
				} else {
					// In the middle
					pages.push('...');
					for (let i = currentPage - 1; i <= currentPage + 1; i++) {
						pages.push(i);
					}
					pages.push('...');
					pages.push(totalPages);
				}
			}

			return pages;
		},
		// Legacy computed property (backward compatible)
		visiblePaginationPages() {
			const pages = [];
			const paginationLength = 7;
			const pageStep = 3;
			let minPage = this.currentPage - pageStep;

			if (minPage < 1) minPage = 1;
			let maxPage = this.currentPage + pageStep;
			if (maxPage < paginationLength) {
				// eslint-disable-next-line no-new-wrappers
				maxPage = new Number(paginationLength);
			}

			if (maxPage > this.paginationPages) {
				// eslint-disable-next-line no-new-wrappers
				maxPage = new Number(this.paginationPages);
			}
			for (let index = minPage; index <= maxPage; index++) {
				pages.push(index);
			}
			if (maxPage > paginationLength) {
				pages.unshift('...');
				pages.unshift(1);
			}
			if (this.currentPage < (this.paginationPages - paginationLength)) {
				pages.push('...');
				pages.push(this.paginationPages);
			}
			return pages;
		},
	},
	methods: {
		handlePageChange(page) {
			const totalPages = this.useModernLayout ? this.totalPages : this.paginationPages;

			// Validate page number
			if (page < 1 || page > totalPages || page === this.currentPage) {
				return;
			}

			if (this.autoScroll) {
				window.scrollTo(0, 0);
			}

			// Emit both event names for backward compatibility
			this.$emit('page-change', page);
			this.$emit('page-changed', page);
		},
	},
};
</script>

<style scoped>
.pagination-component {
	width: 100%;
}

/* Modern wrapper styles */
.pagination-wrapper {
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 1rem;
}

/* Pagination styles */
.pagination .page-link {
	color: #495057;
	border-color: #dee2e6;
	padding: 0.5rem 0.75rem;
	transition: all 0.2s ease-in-out;
}

.pagination-md .page-link {
	padding: 0.625rem 0.875rem;
}

.pagination-lg .page-link {
	padding: 0.75rem 1rem;
	font-size: 1.125rem;
}

.pagination .page-item.active .page-link {
	background-color: #f28901;
	border-color: #f28901;
	color: #fff;
}

.pagination .page-item.disabled .page-link {
	color: #6c757d;
	pointer-events: none;
	background-color: #fff;
	border-color: #dee2e6;
}

.pagination .page-link:hover:not(.active):not(.disabled) {
	color: #f28901;
	background-color: #f8f9fa;
	border-color: #dee2e6;
}

.pagination .page-item.active .page-link:hover {
	background-color: #e07a00;
	border-color: #e07a00;
	color: #fff;
}

.pagination .page-link i {
	vertical-align: middle;
}

/* Responsive adjustments */
@media (max-width: 576px) {
	.pagination-wrapper {
		flex-direction: column;
		align-items: flex-start !important;
	}

	.pagination-wrapper nav {
		width: 100%;
	}

	.pagination {
		justify-content: center;
	}
}

/* Legacy styles (backward compatible) */
.block-footer {
	width: 100%;
}
</style>
